import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Trophy, 
  Crown, 
  ChevronLeft, 
  MessageCircle, 
  PlusCircle,
  ArrowUpRight, 
  Star, 
  Clock, 
  Sparkles, 
  Search,
  X,
  Calendar,
  Award
} from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { classifyConversation } from '../../utils/conversationClassifier';

// Enhanced ConversationItem component - Optimized for performance
const ConversationItem = React.memo(({ 
  conversation, 
  isActive, 
  onSelect, 
  index
}) => {
  // Get conversation category style based on type
  const categoryStyle = useMemo(() => {
    return classifyConversation(
      conversation.metadata?.title,
      conversation.lastMessage
    );
  }, [conversation.metadata?.title, conversation.lastMessage]);
  
  const { icon: CategoryIcon, color, bgColor, label } = categoryStyle;
  
  // Use a stable callback to prevent recreating on every render
  const handleClick = useCallback(() => {
    onSelect(conversation);
  }, [conversation, onSelect]);
  
  return (
    <motion.button
      key={conversation.id}
      onClick={handleClick}
      className={`w-full p-3.5 rounded-xl transition-all duration-200 flex items-center gap-3 relative
        ${isActive 
          ? 'bg-gradient-to-r from-emerald-500/20 to-emerald-400/10 border border-emerald-400/20' 
          : 'bg-white/5 hover:bg-white/10 border border-white/5'}`}
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(index * 0.03, 0.3) }} // Cap animation delay
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      layout // Smooth transitions when items are added/removed by filtering
    >
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${bgColor} ${isActive ? 'scale-110' : ''}`}>
        <CategoryIcon className={`w-5 h-5 ${color}`} />
      </div>
      
      <div className="flex-1 min-w-0 text-left">
        <div className="flex items-center gap-2">
          <span className={`text-sm font-medium truncate ${isActive ? 'text-emerald-300' : 'text-white/85'}`}>
            {conversation.metadata?.title || 'Untitled Chat'}
          </span>
          {label && (
            <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium ${bgColor} ${color}`}>
              {label}
            </span>
          )}
        </div>
        <p className="text-xs text-white/65 truncate mt-0.5">
          {conversation.lastMessage || 'No messages yet'}
        </p>
        <div className="flex justify-between mt-1 text-[10px] text-white/55">
          <div className="flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            <span>{formatDate(conversation.metadata?.updatedAt)}</span>
          </div>
          <div>
            {conversation.metadata?.messageCount || 0} msgs
          </div>
        </div>
      </div>
      
      {isActive && (
        <div className="absolute inset-0 rounded-xl pointer-events-none">
          <div className="absolute inset-0 rounded-xl border border-emerald-400/30" />
          
          {/* Subtle shimmer effect */}
          <motion.div
            className="absolute inset-0 rounded-xl overflow-hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <motion.div
              className="absolute inset-0 translate-x-full"
              style={{
                background: "linear-gradient(90deg, transparent, rgba(16, 185, 129, 0.1), transparent)",
                backgroundSize: "200% 100%"
              }}
              animate={{ translateX: ['-100%', '100%'] }}
              transition={{ 
                duration: 2, 
                repeat: Infinity,
                ease: "linear" 
              }}
            />
          </motion.div>
        </div>
      )}
    </motion.button>
  );
});

ConversationItem.displayName = 'ConversationItem';

// Format date helper function
const formatDate = (timestamp) => {
  if (!timestamp) return '';
  
  const date = new Date(timestamp);
  const now = new Date();
  
  // Today
  if (date.toDateString() === now.toDateString()) {
    return `Today, ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  }
  
  // Yesterday
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) {
    return `Yesterday, ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  }
  
  // Within a week
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const dayDiff = Math.floor((now - date) / (1000 * 60 * 60 * 24));
  if (dayDiff < 7) {
    return `${dayNames[date.getDay()]}, ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  }
  
  // Older
  return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

// Extract stats card to reduce main component complexity
const StatsCard = React.memo(({ userStats, isExpanded, toggleExpanded }) => {
  // Always render with default values if needed
  
  return (
    <>
      <motion.div 
        className="relative p-4 mb-2"
        onClick={toggleExpanded}
        whileTap={{ scale: 0.98 }}
      >
        {/* Glassmorphism background with gradients */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-800/80 to-slate-900/80 rounded-xl overflow-hidden backdrop-blur-md border border-white/5">
          <div className="absolute inset-0" style={{
            background: `radial-gradient(circle at 20% 20%, rgb(16 185 129 / 0.3) 0%, transparent 50%),
                        radial-gradient(circle at 80% 80%, rgb(16 185 129 / 0.1) 0%, transparent 50%)`
          }}/>
        </div>

        <div className="relative flex items-center gap-3 z-10">
          <motion.div
            className="relative rounded-2xl p-3 bg-gradient-to-br from-emerald-600 to-emerald-700"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Trophy className="w-8 h-8 text-white" />
            {/* Subtle glow effect */}
            <motion.div
              className="absolute inset-0 rounded-2xl bg-emerald-400/40"
              animate={{
                opacity: [0.2, 0.4, 0.2],
                scale: [1, 1.1, 1],
              }}
              transition={{ duration: 2, repeat: Infinity }}
              style={{ filter: 'blur(8px)', zIndex: -1 }}
            />
          </motion.div>

          <div className="flex-1">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <span className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-emerald-300 to-emerald-100">
                  {userStats.points?.total?.toLocaleString() || 0}
                </span>
                <motion.div
                  animate={{ rotate: [0, 10, -10, 0] }}
                  transition={{ duration: 5, repeat: Infinity, repeatDelay: 3 }}
                >
                  <Sparkles className="w-4 h-4 text-yellow-400 opacity-80" />
                </motion.div>
              </div>
              <motion.div
                className="bg-emerald-500/20 px-2.5 py-1 rounded-full text-xs font-medium text-emerald-400 flex items-center gap-1.5"
                animate={{ y: [0, -2, 0], opacity: [0.7, 1, 0.7] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <ArrowUpRight className="w-3 h-3" />
                <span>+{userStats.points?.daily || 0} today</span>
              </motion.div>
            </div>

            <div className="relative w-full h-2 bg-white/5 rounded-full mt-2.5 overflow-hidden">
              {/* Progress bar */}
              <motion.div
                className="absolute h-full rounded-full bg-gradient-to-r from-emerald-500 to-emerald-400"
                initial={{ width: 0 }}
                animate={{ width: `${userStats.rank?.progress || 0}%` }}
                transition={{ duration: 0.8 }}
              />
              {/* Shimmer effect */}
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                animate={{ x: ['-100%', '100%'] }}
                transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
              />
            </div>

            <div className="flex items-center justify-between text-sm mt-1.5">
              <div className="flex items-center gap-1 text-emerald-400">
                <Crown className="w-4 h-4" />
                <span className="font-medium">{userStats.rank?.current || 'Rookie'}</span>
              </div>
              <motion.div
                className="text-white/65 flex items-center gap-1"
                animate={{ opacity: [0.65, 0.85, 0.65] }}
                transition={{ duration: 3, repeat: Infinity }}
              >
                <span className="text-xs">{userStats.rank?.next || 'Pro'} →</span>
                <div className="w-1 h-1 rounded-full bg-emerald-400"></div>
                <span className="text-xs">{userStats.rank?.progress || 0}%</span>
              </motion.div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Expandable Stats Section */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden border-t border-b border-white/5"
          >
            <div className="grid grid-cols-2 gap-3 p-4 bg-white/5">
              <div className="rounded-lg bg-white/5 p-3 flex flex-col">
                <span className="text-white/65 text-xs">Streak Days</span>
                <div className="flex items-center gap-1.5 mt-1">
                  <Star className="w-4 h-4 text-yellow-400" />
                  <span className="text-xl font-medium text-white">
                    {userStats.points?.streak || 0}
                  </span>
                </div>
              </div>

              <div className="rounded-lg bg-white/5 p-3 flex flex-col">
                <span className="text-white/65 text-xs">Thumbs Up</span>
                <div className="flex items-center gap-1.5 mt-1">
                  <Award className="w-4 h-4 text-emerald-400" />
                  <span className="text-xl font-medium text-white">
                    {userStats.achievements?.thumbsUpReceived || 0}
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
});

StatsCard.displayName = 'StatsCard';

// Improved search component
const SearchBar = React.memo(({ searchTerm, setSearchTerm, clearSearch }) => {
  return (
    <div className="sticky top-0 z-10 bg-slate-900/80 backdrop-blur-sm px-4 py-2">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-400/70" />
        <input
          type="text"
          placeholder="Search conversations..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full bg-white/5 border border-white/10 rounded-lg pl-10 pr-10 py-2.5
            text-white placeholder-white/60
            focus:outline-none focus:ring-2 focus:ring-emerald-500/50
            focus:border-emerald-500/30
            transition-all duration-300"
        />
        {searchTerm && (
          <button
            onClick={clearSearch}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-white/50 hover:text-white/80 p-1 rounded-full hover:bg-white/5"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
});

SearchBar.displayName = 'SearchBar';

// Empty state component
const EmptyState = ({ searchTerm, clearSearch, onNewChat }) => {
  if (searchTerm) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center">
        <Search className="w-8 h-8 text-white/30 mb-2" />
        <p className="text-white/60">No conversations match your search</p>
        <motion.button
          onClick={clearSearch}
          className="mt-2 text-emerald-400 text-sm bg-emerald-500/10 px-3 py-1 rounded-lg hover:bg-emerald-500/20 transition-colors"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          Clear search
        </motion.button>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center py-8 text-center">
      <MessageCircle className="w-8 h-8 text-white/30 mb-2" />
      <p className="text-white/60">No conversations yet</p>
      <motion.button
        onClick={onNewChat}
        className="mt-3 px-4 py-2 bg-emerald-500/10 hover:bg-emerald-500/20
          text-emerald-400 rounded-lg transition-all duration-300"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        Start a new conversation
      </motion.button>
    </div>
  );
};

// Loading indicator component
const LoadingIndicator = () => (
  <div className="flex items-center justify-center h-40">
    <div className="flex space-x-2">
      {[0, 0.2, 0.4].map((delay, i) => (
        <motion.div 
          key={i}
          className="w-3 h-3 bg-emerald-500 rounded-full" 
          animate={{
            y: [0, -10, 0],
            opacity: [0.5, 1, 0.5]
          }}
          transition={{
            duration: 1,
            repeat: Infinity,
            delay
          }}
        />
      ))}
    </div>
  </div>
);

// Main MobileStatsPanel component
const MobileStatsPanel = ({ 
  userId, 
  onClose, 
  onNewChat, 
  conversations = [], 
  activeConversationId,
  onChatSelect 
}) => {
  // State - Initialize with default stats to prevent loading flash
  const [userStats, setUserStats] = useState({
    points: { total: 0, daily: 0, streak: 0 },
    rank: { current: 'Rookie', next: 'Pro', progress: 0 },
    achievements: { thumbsUpReceived: 0 }
  });
  const [isExpanded, setIsExpanded] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Fetch user stats on mount without blocking UI
  useEffect(() => {
    const fetchUserStats = async () => {
      try {
        const userStatsRef = doc(db, "userStats", userId);
        const userStatsDoc = await getDoc(userStatsRef);
        
        if (userStatsDoc.exists()) {
          setUserStats(userStatsDoc.data());
        }
      } catch (error) {
        console.error("Error fetching user stats:", error);
      }
    };

    fetchUserStats();
  }, [userId]);
  
  // Toggle expanded state
  const toggleExpanded = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, []);
  
  // Clear search handler
  const clearSearch = useCallback(() => {
    setSearchTerm('');
  }, []);
  
  // Filter conversations based on search term
  const filteredConversations = useMemo(() => {
    if (!searchTerm) return conversations;
    
    const query = searchTerm.toLowerCase();
    return conversations.filter(conv => 
      (conv.metadata?.title || '').toLowerCase().includes(query) ||
      (conv.lastMessage || '').toLowerCase().includes(query)
    );
  }, [conversations, searchTerm]);

  // Remove loading state - component always renders immediately

  return (
    <motion.div 
      className="fixed inset-0 z-40 bg-gradient-to-b from-slate-900 via-slate-900/98 to-slate-900/95 backdrop-blur-lg pointer-events-none"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
    >
      <div className="flex flex-col h-full pt-safe pointer-events-auto">
        {/* Header with glassy effect */}
        <div className="relative">
          <div className="absolute inset-0 overflow-hidden">
            <motion.div 
              className="absolute -top-10 -right-10 w-40 h-40 bg-emerald-500/20 rounded-full opacity-50"
              animate={{
                scale: [1, 1.1, 1],
                opacity: [0.5, 0.3, 0.5]
              }}
              transition={{
                duration: 5,
                repeat: Infinity,
                ease: "easeInOut"
              }}
              style={{ filter: 'blur(30px)' }}
            />
          </div>
          
          <div className="relative flex items-center justify-between p-4 border-b border-white/10">
            <button
              onClick={onClose}
              className="flex items-center gap-2 text-white/85 hover:text-white transition-colors"
            >
              <ChevronLeft className="w-5 h-5" />
              <span className="font-medium">Back to Chat</span>
            </button>
            <motion.button
              onClick={onNewChat}
              className="p-2 text-emerald-400 hover:text-emerald-300 transition-all
                hover:bg-emerald-500/20 rounded-lg"
              aria-label="New Chat"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <PlusCircle className="w-5 h-5" />
            </motion.button>
          </div>
        </div>

        {/* Stats Card */}
        <StatsCard 
          userStats={userStats} 
          isExpanded={isExpanded}
          toggleExpanded={toggleExpanded}
        />

        {/* Search Bar */}
        <SearchBar 
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          clearSearch={clearSearch}
        />

        {/* Conversation List */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-4 space-y-2.5">
            {filteredConversations.length === 0 ? (
              <EmptyState 
                searchTerm={searchTerm}
                clearSearch={clearSearch}
                onNewChat={onNewChat}
              />
            ) : (
              filteredConversations.map((conversation, index) => (
                <ConversationItem
                  key={conversation.id}
                  conversation={conversation}
                  isActive={conversation.id === activeConversationId}
                  onSelect={(conv) => {
                    onChatSelect(conv);
                    onClose();
                  }}
                  index={index}
                />
              ))
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default React.memo(MobileStatsPanel);

// // Enhanced mobile stats panel with improved visuals
// import React, { useState, useEffect } from 'react';
// import { motion, AnimatePresence } from 'framer-motion';
// import { 
//   Trophy, 
//   Crown, 
//   ChevronLeft, 
//   MessageCircle,
//   PlusCircle,
//   ArrowUpRight,
//   Star,
//   Clock,
//   Award,
//   TrendingUp,
//   Target,
//   Sparkles,
//   Search
// } from 'lucide-react';
// import { doc, getDoc } from 'firebase/firestore';
// import { db } from '../../firebase';

// const MobileStatsPanel = ({ 
//     userId, 
//     onClose, 
//     onNewChat, 
//     conversations = [], 
//     activeConversationId,
//     onChatSelect 
//   }) => {
//   const [userStats, setUserStats] = useState(null);
//   const [isExpanded, setIsExpanded] = useState(false);
//   const [searchTerm, setSearchTerm] = useState('');
//   const [showSearchBar, setShowSearchBar] = useState(false);

//   useEffect(() => {
//     const fetchUserStats = async () => {
//       const userStatsRef = doc(db, "userStats", userId);
//       const userStatsDoc = await getDoc(userStatsRef);
//       if (userStatsDoc.exists()) setUserStats(userStatsDoc.data());
//     };
//     fetchUserStats();
//   }, [userId]);

//   // Filter conversations based on search term
//   const filteredConversations = searchTerm
//     ? conversations.filter(conv => 
//         (conv.metadata?.title || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
//         (conv.lastMessage || '').toLowerCase().includes(searchTerm.toLowerCase())
//       )
//     : conversations;

//   if (!userStats) return null;

//   // Get conversation category icon and color
//   const getChatCategoryStyle = (conversation) => {
//     const title = (conversation.metadata?.title || '').toLowerCase();
    
//     if (title.includes('prediction')) {
//       if (title.includes('win')) {
//         return { 
//           icon: Award, 
//           color: 'text-green-400',
//           bgColor: 'bg-green-500/20',
//           label: 'Win'
//         };
//       } else if (title.includes('pending')) {
//         return { 
//           icon: Clock, 
//           color: 'text-yellow-400',
//           bgColor: 'bg-yellow-500/20',
//           label: 'Pending'
//         };
//       } else {
//         return { 
//           icon: TrendingUp, 
//           color: 'text-red-400',
//           bgColor: 'bg-red-500/20',
//           label: 'Loss'
//         };
//       }
//     } else if (title.includes('analysis')) {
//       return { 
//         icon: Target, 
//         color: 'text-blue-400',
//         bgColor: 'bg-blue-500/20'
//       };
//     }
    
//     return { 
//       icon: MessageCircle, 
//       color: 'text-emerald-400',
//       bgColor: 'bg-emerald-500/20'
//     };
//   };

//   return (
//     <motion.div 
//       className="fixed inset-0 z-50 bg-gradient-to-b from-slate-900 via-slate-900/98 to-slate-900/95 backdrop-blur-lg"
//       initial={{ opacity: 0, y: 20 }}
//       animate={{ opacity: 1, y: 0 }}
//       exit={{ opacity: 0, y: 20 }}
//       transition={{ type: "spring", damping: 25, stiffness: 120 }}
//     >
//       <div className="flex flex-col h-full pt-safe">
//         {/* Header */}
//         <div className="relative">
//           {/* Background glow effect */}
//           <div className="absolute inset-0 overflow-hidden">
//             <div className="absolute -top-10 -right-10 w-40 h-40 bg-emerald-500/20 blur-2xl rounded-full opacity-50" />
//             <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-emerald-600/20 blur-2xl rounded-full opacity-30" />
//           </div>
          
//           <div className="relative flex items-center justify-between p-4 border-b border-white/10">
//             <button
//               onClick={onClose}
//               className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
//             >
//               <ChevronLeft className="w-5 h-5" />
//               <span className="font-medium">Back to Chat</span>
//             </button>
//             <button
//               onClick={onNewChat}
//               className="p-2 text-emerald-400 hover:text-emerald-300 transition-all
//                 hover:bg-emerald-500/20 rounded-lg active:scale-95"
//               aria-label="New Chat"
//             >
//               <PlusCircle className="w-5 h-5" />
//             </button>
//           </div>
//         </div>

//         {/* Stats Panel */}
//         <motion.div 
//           className="relative p-4 mb-2"
//           onClick={() => setIsExpanded(!isExpanded)}
//           whileTap={{ scale: 0.98 }}
//         >
//           {/* Dynamic background */}
//           <div className="absolute inset-0 bg-white/5 rounded-xl overflow-hidden">
//             <motion.div
//               className="absolute inset-0"
//               style={{
//                 background: `
//                   radial-gradient(circle at 20% 20%, rgb(16 185 129 / 0.3) 0%, transparent 50%),
//                   radial-gradient(circle at 80% 80%, rgb(16 185 129 / 0.2) 0%, transparent 40%)
//                 `
//               }}
//               animate={{
//                 backgroundPosition: ['0% 0%', '100% 100%', '0% 0%']
//               }}
//               transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
//             />
//           </div>

//           <div className="relative flex items-center gap-3 z-10">
//             <motion.div 
//               className="relative rounded-2xl p-3 bg-gradient-to-br from-emerald-600 to-emerald-700"
//               whileHover={{ scale: 1.05 }}
//               whileTap={{ scale: 0.95 }}
//             >
//               <Trophy className="w-8 h-8 text-white" />
//               <motion.div
//                 className="absolute inset-0 rounded-2xl bg-emerald-400/40"
//                 animate={{
//                   opacity: [0.2, 0.4, 0.2],
//                   scale: [1, 1.1, 1],
//                 }}
//                 transition={{ duration: 2, repeat: Infinity }}
//                 style={{ filter: 'blur(8px)', zIndex: -1 }}
//               />
//             </motion.div>

//             <div className="flex-1">
//               <div className="flex items-center justify-between">
//                 <div className="flex items-center gap-1.5">
//                   <span className="text-2xl font-bold text-white">
//                     {userStats.points.total.toLocaleString()}
//                   </span>
//                   <motion.div
//                     animate={{ rotate: [0, 10, -10, 0] }}
//                     transition={{ duration: 5, repeat: Infinity, repeatDelay: 3 }}
//                   >
//                     <Sparkles className="w-4 h-4 text-yellow-400 opacity-80" />
//                   </motion.div>
//                 </div>
//                 <motion.div
//                   className="bg-emerald-500/20 px-2.5 py-1 rounded-full text-xs font-medium text-emerald-400 flex items-center gap-1.5"
//                   animate={{ y: [0, -2, 0], opacity: [0.7, 1, 0.7] }}
//                   transition={{ duration: 2, repeat: Infinity }}
//                 >
//                   <ArrowUpRight className="w-3 h-3" />
//                   <span>+{userStats.points.daily} today</span>
//                 </motion.div>
//               </div>

//               <div className="relative w-full h-2 bg-white/5 rounded-full mt-2.5 overflow-hidden">
//                 <motion.div
//                   className="absolute h-full rounded-full bg-gradient-to-r from-emerald-500 to-emerald-400"
//                   initial={{ width: 0 }}
//                   animate={{ width: `${userStats.rank.progress}%` }}
//                 />
//                 <motion.div
//                   className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
//                   animate={{ x: ['-100%', '100%'] }}
//                   transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
//                 />
//               </div>

//               <div className="flex items-center justify-between text-sm mt-1.5">
//                 <div className="flex items-center gap-1 text-emerald-400">
//                   <Crown className="w-4 h-4" />
//                   <span className="font-medium">{userStats.rank.current}</span>
//                 </div>
//                 <motion.div 
//                   className="text-white/50 flex items-center gap-1"
//                   animate={{ opacity: [0.5, 0.8, 0.5] }}
//                   transition={{ duration: 3, repeat: Infinity }}
//                 >
//                   <span className="text-xs">{userStats.rank.next} →</span>
//                   <div className="w-1 h-1 rounded-full bg-emerald-400"></div>
//                   <span className="text-xs">{userStats.rank.progress}%</span>
//                 </motion.div>
//               </div>
//             </div>
//           </div>
//         </motion.div>

//         {/* Expanded Stats Section */}
//         <AnimatePresence>
//           {isExpanded && (
//             <motion.div
//               initial={{ height: 0, opacity: 0 }}
//               animate={{ height: 'auto', opacity: 1 }}
//               exit={{ height: 0, opacity: 0 }}
//               className="overflow-hidden border-t border-b border-white/5"
//             >
//               <div className="grid grid-cols-2 gap-3 p-4 bg-white/5">
//                 <div className="rounded-lg bg-white/5 p-3 flex flex-col">
//                   <span className="text-white/50 text-xs">Streak Milestones</span>
//                   <div className="flex items-center gap-1.5 mt-1">
//                     <Star className="w-4 h-4 text-yellow-400" />
//                     <span className="text-xl font-medium text-white">
//                       {userStats.achievements?.streakMilestones?.length || 0}
//                     </span>
//                   </div>
//                 </div>
                
//                 <div className="rounded-lg bg-white/5 p-3 flex flex-col">
//                   <span className="text-white/50 text-xs">Thumbs Up</span>
//                   <div className="flex items-center gap-1.5 mt-1">
//                     <Award className="w-4 h-4 text-emerald-400" />
//                     <span className="text-xl font-medium text-white">
//                       {userStats.achievements?.thumbsUpReceived || 0}
//                     </span>
//                   </div>
//                 </div>
//               </div>
//             </motion.div>
//           )}
//         </AnimatePresence>

//         {/* Search Bar */}
//         <div className="sticky top-0 z-10 bg-slate-900/80 backdrop-blur-sm px-4 py-2">
//           <div className="relative">
//             <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-400/70" />
//             <input
//               type="text"
//               placeholder="Search conversations..."
//               value={searchTerm}
//               onChange={(e) => setSearchTerm(e.target.value)}
//               className="w-full bg-white/5 border border-white/10 rounded-lg pl-10 pr-4 py-2.5
//                 text-white placeholder-white/40
//                 focus:outline-none focus:ring-2 focus:ring-emerald-500/50
//                 focus:border-emerald-500/30
//                 transition-all duration-300"
//             />
//           </div>
//         </div>

//         {/* Conversation List */}
//         <div className="flex-1 overflow-y-auto">
//           <div className="p-4 space-y-2.5">
//             {filteredConversations.length === 0 ? (
//               <div className="flex flex-col items-center justify-center py-8 text-center">
//                 {searchTerm ? (
//                   <>
//                     <Search className="w-8 h-8 text-white/20 mb-2" />
//                     <p className="text-white/40">No conversations match your search</p>
//                     <button 
//                       onClick={() => setSearchTerm('')}
//                       className="mt-2 text-emerald-400 text-sm"
//                     >
//                       Clear search
//                     </button>
//                   </>
//                 ) : (
//                   <>
//                     <MessageCircle className="w-8 h-8 text-white/20 mb-2" />
//                     <p className="text-white/40">No conversations yet</p>
//                     <button 
//                       onClick={onNewChat}
//                       className="mt-3 px-4 py-2 bg-emerald-500/10 hover:bg-emerald-500/20
//                         text-emerald-400 rounded-lg transition-all duration-300"
//                     >
//                       Start a new conversation
//                     </button>
//                   </>
//                 )}
//               </div>
//             ) : (
//               filteredConversations.map((conversation, index) => {
//                 const { icon: CategoryIcon, color, bgColor, label } = getChatCategoryStyle(conversation);
//                 const isActive = conversation.id === activeConversationId;
                
//                 return (
//                   <motion.button
//                     key={conversation.id}
//                     onClick={() => {
//                       onChatSelect(conversation);
//                       onClose();
//                     }}
//                     className={`w-full p-3.5 rounded-xl transition-all duration-200 flex items-center gap-3 relative
//                       ${isActive 
//                         ? 'bg-gradient-to-r from-emerald-500/20 to-emerald-400/10 border border-emerald-400/20' 
//                         : 'bg-white/5 hover:bg-white/10 border border-white/5'}`}
//                     initial={{ opacity: 0, y: 15 }}
//                     animate={{ opacity: 1, y: 0 }}
//                     transition={{ delay: index * 0.05 }}
//                     whileHover={{ scale: 1.02 }}
//                     whileTap={{ scale: 0.98 }}
//                   >
//                     <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${bgColor} ${isActive ? 'scale-110' : ''}`}>
//                       <CategoryIcon className={`w-5 h-5 ${color}`} />
//                     </div>
                    
//                     <div className="flex-1 min-w-0 text-left">
//                       <div className="flex items-center gap-2">
//                         <span className={`text-sm font-medium truncate ${isActive ? 'text-emerald-300' : 'text-white/80'}`}>
//                           {conversation.metadata?.title || 'Untitled Chat'}
//                         </span>
//                         {label && (
//                           <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium ${bgColor} ${color}`}>
//                             {label}
//                           </span>
//                         )}
//                       </div>
//                       <p className="text-xs text-white/50 truncate mt-0.5">
//                         {conversation.lastMessage || 'No messages yet'}
//                       </p>
//                     </div>
                    
//                     {isActive && (
//                       <motion.div 
//                         className="absolute inset-0 rounded-xl pointer-events-none"
//                         initial={{ opacity: 0 }}
//                         animate={{ opacity: 1 }}
//                         transition={{ duration: 0.3 }}
//                       >
//                         <div className="absolute inset-0 rounded-xl border border-emerald-400/30" />
//                         <div className="absolute -inset-0.5 rounded-xl opacity-20" style={{ 
//                           background: 'radial-gradient(circle at center, rgba(16, 185, 129, 0.4) 0%, transparent 70%)' 
//                         }} />
//                       </motion.div>
//                     )}
//                   </motion.button>
//                 );
//               })
//             )}
//           </div>
//         </div>
//       </div>
//     </motion.div>
//   );
// };

// export default React.memo(MobileStatsPanel);